import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import {
  Text,
  Card,
  useTheme,
  ActivityIndicator,
  IconButton,
} from 'react-native-paper';
import {
  Coffee,
  Play,
  BookOpen,
  Users,
  Target,
  Brain,
  Award,
  Search,
  Heart,
  Settings,
  TrendingUp,
  Calendar,
} from 'lucide-react-native';

import { logger } from '../../utils/logger';
import type {
  DashboardInsights,
  ContentRecommendation,
} from '@coffeeplexity/shared';

interface QuickAction {
  id: string;
  type: string;
  title: string;
  icon: React.ComponentType<any>;
  color: string;
  priority: number;
  contentId?: string;
  activityId?: string;
}

interface AdaptiveQuickActionsProps {
  insights?: DashboardInsights | null;
  recommendations: ContentRecommendation[];
  onActionPress: (actionType: string, actionId?: string) => void;
  loading?: boolean;
}

export function AdaptiveQuickActions({
  insights,
  recommendations,
  onActionPress,
  loading = false,
}: AdaptiveQuickActionsProps) {
  const theme = useTheme();

  // Generate adaptive quick actions based on user behavior patterns and insights
  const adaptiveActions = useMemo((): QuickAction[] => {
    const baseActions: QuickAction[] = [
      {
        id: 'recipes',
        type: 'browse_recipes',
        title: 'Recipes',
        icon: Coffee,
        color: '#8B4513', // Brown
        priority: 0.8,
      },
      {
        id: 'videos',
        type: 'browse_videos',
        title: 'Videos',
        icon: Play,
        color: '#FF6B35', // Orange
        priority: 0.7,
      },
      {
        id: 'journey',
        type: 'continue_journey',
        title: 'Journey',
        icon: Target,
        color: '#4ECDC4', // Teal
        priority: 0.9,
      },
      {
        id: 'search',
        type: 'search',
        title: 'Search',
        icon: Search,
        color: '#95A5A6', // Gray
        priority: 0.5,
      },
    ];

    // Add contextual actions based on insights
    if (insights) {
      // Add skill practice if there are skill gaps
      const skillGaps = insights.progressInsights.filter(
        insight => insight.type === 'challenge'
      );

      if (skillGaps.length > 0) {
        baseActions.push({
          id: 'practice',
          type: 'practice_skill',
          title: 'Practice',
          icon: Brain,
          color: '#9B59B6', // Purple
          priority: 0.85,
        });
      }

      // Add achievements if there are recent accomplishments
      const recentAchievements = insights.progressInsights.filter(
        insight => insight.type === 'achievement'
      );

      if (recentAchievements.length > 0) {
        baseActions.push({
          id: 'achievements',
          type: 'view_achievements',
          title: 'Awards',
          icon: Award,
          color: '#FFD700', // Gold
          priority: 0.6,
        });
      }

      // Add social actions if user is engaged with community
      if (insights.streakInfo?.current > 7) {
        baseActions.push({
          id: 'social',
          type: 'browse_social',
          title: 'Community',
          icon: Users,
          color: '#3498DB', // Blue
          priority: 0.65,
        });
      }

      // Add favorites if user has saved content
      baseActions.push({
        id: 'favorites',
        type: 'view_favorites',
        title: 'Favorites',
        icon: Heart,
        color: '#E74C3C', // Red
        priority: 0.55,
      });
    }

    // Add actions based on high-priority recommendations
    const highPriorityRecs = recommendations.filter(rec => rec.priority >= 0.8);

    highPriorityRecs.slice(0, 2).forEach((rec, index) => {
      baseActions.push({
        id: `rec_${rec.id}`,
        type: 'try_recommendation',
        title: rec.contentType === 'recipe' ? 'Try Recipe' : 'Watch Video',
        icon: rec.contentType === 'recipe' ? Coffee : Play,
        color: '#27AE60', // Green
        priority: 0.75 + index * 0.05,
        contentId: rec.contentId,
      });
    });

    // Sort by priority and return top 6 actions
    return baseActions.sort((a, b) => b.priority - a.priority).slice(0, 6);
  }, [insights, recommendations]);

  const handleActionPress = (action: QuickAction) => {
    switch (action.type) {
      case 'browse_recipes':
        onActionPress('browse_recipes');
        break;
      case 'browse_videos':
        onActionPress('browse_videos');
        break;
      case 'continue_journey':
        onActionPress('continue_journey');
        break;
      case 'practice_skill':
        onActionPress('practice_skill');
        break;
      case 'view_achievements':
        onActionPress('view_achievements');
        break;
      case 'browse_social':
        onActionPress('browse_social');
        break;
      case 'view_favorites':
        onActionPress('view_favorites');
        break;
      case 'search':
        onActionPress('search');
        break;
      case 'try_recommendation':
        onActionPress('try_recipe', action.contentId);
        break;
      default:
        logger.warn('Unknown action type:', action.type);
    }
  };

  if (loading) {
    return (
      <Card
        style={[styles.container, { backgroundColor: theme.colors.surface }]}
      >
        <Card.Content style={styles.loadingContent}>
          <ActivityIndicator size='large' color={theme.colors.primary} />
          <Text
            variant='bodyMedium'
            style={[
              styles.loadingText,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Personalizing your actions...
          </Text>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <Card.Content style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <Text
            variant='titleMedium'
            style={[styles.title, { color: theme.colors.onSurface }]}
          >
            Quick Actions
          </Text>

          <Text
            variant='bodySmall'
            style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
          >
            Personalized for you
          </Text>
        </View>

        {/* Actions Grid */}
        <View style={styles.actionsGrid}>
          {adaptiveActions.map(action => {
            const ActionIcon = action.icon;

            return (
              <View key={action.id} style={styles.actionContainer}>
                <IconButton
                  icon={() => <ActionIcon size={24} color='white' />}
                  size={28}
                  onPress={() => handleActionPress(action)}
                  style={[
                    styles.actionButton,
                    { backgroundColor: action.color },
                  ]}
                />

                <Text
                  variant='bodySmall'
                  style={[
                    styles.actionLabel,
                    { color: theme.colors.onSurface },
                  ]}
                  numberOfLines={1}
                >
                  {action.title}
                </Text>
              </View>
            );
          })}
        </View>

        {/* AI Insight */}
        {insights && (
          <View style={styles.insightContainer}>
            <TrendingUp size={14} color={theme.colors.primary} />
            <Text
              variant='bodySmall'
              style={[
                styles.insightText,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Actions adapted based on your learning patterns
            </Text>
          </View>
        )}
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  content: {
    paddingVertical: 20,
  },
  loadingContent: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    marginTop: 12,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontWeight: '600',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 12,
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  actionContainer: {
    width: '30%',
    alignItems: 'center',
    marginBottom: 16,
    minHeight: 80,
  },
  actionButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    marginBottom: 8,
  },
  actionLabel: {
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '500',
    lineHeight: 14,
    paddingHorizontal: 4,
  },
  insightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
    gap: 6,
  },
  insightText: {
    fontSize: 11,
    fontStyle: 'italic',
  },
});
